import { useInvalidation } from 'api-read-hook';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';
import { Api, useApiFormSubmit } from 'src/api';
import { Modal, ModalBody, ModalFooter, Text } from 'src/components';
import { Style } from 'src/constants';
import { useSelectedCompany } from 'src/contexts';
import {
  FieldCheckbox,
  FieldRadioGroup,
  FieldText,
  SubmitButton,
} from 'src/form';
import { capitalize } from 'src/util';

type Account = Api.Environment.V['accounts'][number];

type Props = {
  environments: Api.Environment.V[];
  existing?: Account | null;
  defaultEnvironmentIri?: string | null;
  onClose: () => void;
};

type FormValues = {
  environment: string | null;
  type: string | null;
  username: string | null;
  password: string | null;
  instructions: string | null;
  isInternal: boolean;
};

export default function AddAccountModal({
  environments,
  existing,
  defaultEnvironmentIri,
  onClose,
}: Props) {
  const { invalidateExact } = useInvalidation();
  const company = useSelectedCompany();

  // Default to the environment of the tab you're on, else the first one.
  const defaultEnvIri = defaultEnvironmentIri ?? environments[0]?.['@id'] ?? '';

  const submit = useApiFormSubmit(
    existing ? 'PATCH' : 'POST',
    existing ? `/environment-accounts/${existing.id}` : '/environment-accounts',
    {
      onSuccess: () => {
        onClose();
        invalidateExact(`/companies/${company.id}/environments`);
      },
    }
  );

  const form = useForm<FormValues>({
    defaultValues: {
      environment: defaultEnvIri,
      type: existing?.type ?? '',
      username: existing?.username ?? '',
      password: existing?.password ?? '',
      instructions: existing?.instructions ?? null,
      isInternal: existing?.isInternal ?? false,
    },
    onSubmit: (values, helpers) => {
      const { environment, ...rest } = values;
      // environment is only sent on create (PATCH can't move an account)
      return submit(existing ? rest : { environment, ...rest }, helpers);
    },
  });

  const envOptions = environments.map((env) => ({
    value: env['@id'],
    label: capitalize(env.slug),
  }));

  return (
    <Modal
      title={existing ? 'Edit account' : 'New account'}
      onClose={onClose}
      fullScreenOnMobile
    >
      <ModalBody>
        {!existing && (
          <FieldRadioGroup
            label="Environment"
            field={form.getField('environment')}
            options={envOptions}
          />
        )}

        <html.div>
          <FieldText
            field={form.getField('type')}
            label="Account Type"
            placeholder="Enter an account type"
            required
          />
          <Text typeface="body.small.regular" color="neutral60">
            e.g Admin, Free user, Subscription user
          </Text>
        </html.div>

        <FieldText
          field={form.getField('username')}
          label="Username"
          placeholder="Enter a username"
          required
        />
        <FieldText
          field={form.getField('password')}
          label="Password"
          placeholder="Enter a password"
          required
        />

        <html.div>
          <FieldText
            field={form.getField('instructions')}
            label="Instructions (Optional)"
            placeholder="Input instructions..."
            multiline
          />
          <Text typeface="body.small.regular" color="neutral60">
            Support markdown
          </Text>
        </html.div>

        <html.label style={[Style.row, Style.alignCenter, styles.checkboxRow]}>
          <FieldCheckbox field={form.getField('isInternal')} />
          <Text typeface="body.regular" color="neutral100">
            Internal only
          </Text>
        </html.label>
      </ModalBody>

      <ModalFooter>
        <SubmitButton {...form} label="Save" />
      </ModalFooter>
    </Modal>
  );
}

const styles = css.create({
  checkboxRow: {
    gap: 8,
    cursor: 'pointer',
  },
});
